import type { ReactElement } from 'react';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';
import { useUIStore } from '@/lib/store';

/*
 * HintTooltip (Osa 3 vihjejärjestelmä): pieni "mitä tämä kontrolli tekee" -toimintovihje.
 * Kääräisee kontrollin (nappi/linkki) ja näyttää lyhyen selosteen hoverilla TAI
 * näppäinfokuksella (Radix). Erillinen EntityTooltipistä, joka näyttää entiteetin DATAA
 * (kampanjan tiedot) -- tämä selittää TOIMINNON.
 *
 * Gated useUIStore.showHints -prefssillä (Settings-toggle "Näytä toimintovihjeet").
 * Kun vihjeet off TAI content tyhjä -> renderöi lapsen suoraan ilman tooltippiä; kontrollin
 * oma aria-label / näkyvä teksti / disabloitu-syy (natiivi title) säilyy -> saavutettavuus
 * ei riipu vihjeistä.
 *
 * Käyttö (asChild-trigger; lapsen on forwardattava ref -- Button/Link käy):
 *
 *   <HintTooltip content={dirty ? undefined : t('hints.tuota.submitApproval')}>
 *     <Button ...>Lähetä hyväksyntään</Button>
 *   </HintTooltip>
 *
 * Disabloitu nappi ei laukaise hoveria (Radix) -> anna disabloidun napin syy natiivina
 * title-attribuuttina ja jätä content tyhjäksi (undefined) disabloidussa tilassa, jolloin
 * HintTooltip ei kääri elementtiä lainkaan.
 *
 * Mobiili: touch ei hoveroi -> vihjeet ovat desktop/näppäimistö-aputoiminto (C4: aria-labelit
 * + näkyvät tekstit kantavat mobiilissa). Wrap-the-control -malli (ei erillistä ⊙-ikonia).
 *
 * Itsenäinen: kääräisee oman TooltipProviderinsa, joten se on drop-in missä tahansa eikä
 * vaadi ulkoista provideria (toimii myös eristetyssä testissä). Provider-konteksti on kevyt;
 * sovelluksen juuren oma TooltipProvider (muille tooltipeille) ei ristiriidassa.
 */

type HintTooltipProps = {
  /** Vihjeteksti (i18n). undefined / tyhjä -> ei vihjettä (renderöi lapsi suoraan). */
  content: string | undefined;
  /** Käärittävä kontrolli. Yksi elementti joka forwardaa refin (Button, Link...). */
  children: ReactElement;
  /** Tooltipin puoli (Radix). Oletus 'top'. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Avautumisviive ms. Oletus 500 (pidempi kuin Providerin 300) -- toimintovihje ei saa
   * olla tunkeileva nopeissa hiiren liikkeissä.
   */
  delayDuration?: number;
};

export function HintTooltip({ content, children, side = 'top', delayDuration = 500 }: HintTooltipProps) {
  const showHints = useUIStore((s) => s.showHints);
  if (!showHints || !content) return children;
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side}>{content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
